import React, { useContext, useEffect, useMemo } from 'react';
import styled from 'styled-components';

import CardContext from 'contexts/CardContext';
import MetricCacheContext from 'contexts/MetricCacheContext';
import useGetMetricIdsFromVis from 'hooks/useGetMetricIdsFromVis';
import isV5ChartDef from 'types/visTypeCheckers/isV5ChartDef';
import ErrorBoundary from 'components/Common/ErrorBoundary';
import Loading from 'components/Loading';
import Typography from 'kingpin/atoms/Typography';
import V5Gadget from 'components/V5Gadget';

// The chart fills whatever the modal gives it — Explore's whole value is a big
// chart, so no fixed height here (unlike the gadget-config preview, which is
// pinned to 400px).
const Wrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  > * {
    flex: 1;
    min-height: 0;
  }
`;

const Centered = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Renders the LIVE gadget for an ad-hoc chart definition.
//
// Metrics are cached on demand, and an unresolved metric fails SILENTLY:
// useUsedMetrics returns [], useV5ChartData then builds an undefined input and
// issues no query at all, leaving a blank chart with no error. MetricsGate can't
// help — its ContentInput union has no ad-hoc variant — so we request and gate
// on the cache directly, the same way ConfigureDashboardGadget does.
const ExploreChartPreview = ({
  chartDef,
}: {
  chartDef: VisualisationDefinition;
}) => {
  const { requestMetricsByIds, isIdResolved } = useContext(MetricCacheContext);
  const getMetricIds = useGetMetricIdsFromVis();

  const metricIds = useMemo(
    () => getMetricIds(chartDef),
    [chartDef, getMetricIds],
  );

  useEffect(() => {
    if (metricIds.length > 0) {
      requestMetricsByIds(metricIds);
    }
  }, [metricIds, requestMetricsByIds]);

  const metricsResolved =
    metricIds.length === 0 || metricIds.every((id) => isIdResolved(id));

  if (!metricsResolved) {
    return (
      <Centered data-testid="explore-chart-loading">
        <Loading />
      </Centered>
    );
  }

  // The config rail can offer gadget types V5Gadget doesn't render (gauge, pace
  // matrix, …). Say so rather than showing an empty box.
  if (!isV5ChartDef(chartDef)) {
    return (
      <Centered data-testid="explore-chart-unsupported">
        <Typography.Body type="Body 12">
          Preview is not available for this gadget type.
        </Typography.Body>
      </Centered>
    );
  }

  return (
    <Wrapper data-testid="explore-chart-preview">
      <CardContext.Provider
        value={{
          chartDefinition: chartDef,
          i: 'na',
          w: 10,
          h: 10,
          // Suppresses the gadget kebab's "Copy to Dashboard" / "Copy to
          // Report" actions, which are dead or redundant outside a dashboard.
          isForConfigureDashboardGadgetPopup: true,
        }}
      >
        <ErrorBoundary>
          <V5Gadget
            chartDefinition={chartDef}
            useSmallNoDataMessage={false}
            key={chartDef.simpleGridConfig ? undefined : chartDef.id}
          />
        </ErrorBoundary>
      </CardContext.Provider>
    </Wrapper>
  );
};

export default ExploreChartPreview;
